<template lang="pug">
  .log-mindfulness
    .log-mindfulness-content
      .log-mindfulness-header How many mindfulness sessions did you practice?
      .enter-mindfulness-input
        .counter-container
          WtSvgIcon(@click.native="decrement" icon="minus" height="37px" width="38px" style="width:38px;height:37px; cursor:pointer" :class="{ 'disabled-icon': mindfulness <= 0 }")
          .centered-input(:class="{ 'error': showError, 'valid': isValid }")
            input(
                type="text"
                name="mindfulness"
                inputmode="numeric"
                pattern="[0-9]*"
                v-model="mindfulness"
                placeholder="0"
                min="1"
                max="20"
                @input="filterDigits"
            )
          WtSvgIcon(@click.native="increment" icon="plus" height="37px" width="38px" style="width:38px;height:37px; cursor:pointer" :class="{ 'disabled-icon': mindfulness >= 20 }")
        .error-message(v-if="showError") The value you have entered is not valid. Check the value and try again.
      .save-button
        WtButton(
          @click.native="saveEntry"
          :loading="savingMindfulness"
          :disabled="!isValid"
        ) Next
</template>

<script>
import { mapActions } from "vuex";
import { dateUtilityMixin } from "@/mixins/dateUtilityMixin";
import analyticsTrackingMixin from "@/mixins/analyticsTrackingMixin";
import WtButton from "@@/WtButton/WtButton";
import WtTitle from "@@/WtTitle/WtTitle";
import WtSvgIcon from "@@/WtSvgIcon/WtSvgIcon";

export default {
  name: "LogMindfulness",

  components: {
    WtTitle,
    WtButton,
    WtSvgIcon
  },

  mixins: [analyticsTrackingMixin, dateUtilityMixin],

  props: {
    uniqueKey: {
      type: String,
      default: null
    },

    entryItem: {
      type: Object,
      default: () => ({}),
      required: true
    },

    entryDate: {
      type: String,
      default: ""
    },

    formData: {
      type: FormData,
      required: true
    },

    saveEntryWrapper: {
      type: Function,
      required: true
    },

    disableSave: {
      type: Boolean,
      default: true
    },

    disableReset: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      mindfulness: 1,
      savingMindfulness: false
    };
  },

  computed: {
    currentMetricValue() {
      let metric = this._.find(this.entryItem.wellnessMetrics, {
        type: "mindfulness"
      });
      return this._.get(metric, "value", "");
    },

    setFormData() {
      if (this._.isEmpty(this.entryItem)) return true;
      return this.mindfulness != this.currentMetricValue;
    },
    isValid() {
      return (
        this.mindfulness && this.mindfulness >= 1 && this.mindfulness <= 20
      );
    },

    showError() {
      return this.mindfulness && !this.isValid;
    }
  },

  watch: {
    mindfulness() {
      if (this.setFormData) {
        this.formData.set("mindfulness", this.mindfulness);
        this.$emit("formDataUpdated", true);
      }
    },

    entryItem: {
      handler() {
        this.mindfulness = this.currentMetricValue;
      },
      immediate: true,
      deep: true
    },

    // Watch for uniqueKey changes to reset the form when switching dates
    uniqueKey: {
      handler() {
        this.mindfulness = this.currentMetricValue;
      },
      immediate: true
    }
  },

  mounted() {
    this.trackFeatureView("tracking", "tracking_mindfulness");
    this.mindfulness = this.currentMetricValue;
  },

  methods: {
    ...mapActions("members", ["storeJournal", "updateJournal"]),
    filterDigits(e) {
      e.target.value = e.target.value.replace(/\D+/g, "");
      this.mindfulness = e.target.value;
    },
    increment() {
      if (this.mindfulness < 20) this.mindfulness++;
    },
    decrement() {
      if (this.mindfulness > 0) this.mindfulness--;
    },

    saveEntry() {
      // Always send the value so the backend links the post to this metric
      this.formData.set("mindfulness", this.mindfulness);

      this.savingMindfulness = true;
      this.saveEntryWrapper(this.storeJournal, this.updateJournal).finally(
        () => {
          this.savingMindfulness = false;
        }
      );
    },

    reset() {
      const item = "mindfulness";
      if (this.formData.has(item)) {
        this.formData.delete(item);
      }
      this.steps = this.currentMetricValue;
      this.$nextTick(() => {
        this.$emit("formReset", true);
      });
    }
  }
};
</script>

<style lang="sass" scoped>
.log-mindfulness
  height: 100%
  display: flex
  flex-direction: column
  justify-content: space-between

.log-mindfulness-content
  flex: 1
  flex-direction: column
  display: flex
  justify-content: center
  align-items: center
  gap: 20px

  .log-mindfulness-header
    width: 100%
    text-align: left
    font-weight: 600
    font-size: 20px
    line-height: 28px
    letter-spacing: 0px
    color: #1B1D20

  .enter-mindfulness-input
    width: 100%

  .counter-container
      display: flex
      align-items: center
      justify-content: space-between
      gap: 10px
      width: 100%

      ::v-deep .control
        width: 100%


  .icon-button
    border-radius: 49px
    width: 38px
    height: 37px
    display: flex
    align-items: center
    justify-content: center

  .save-button
    width: 100%

    ::v-deep .button
      width: 100% !important
      justify-content: center
      font-size: 16px
      line-height: 24px
      letter-spacing: 0px
      background-color: #2972F0
      font-weight: 600

      &:disabled
        background-color: #D1D5DB !important
        border-color: #D1D5DB !important
        color: #9CA3AF !important
        cursor: not-allowed


.centered-input
  border: 1px solid #e5e7eb
  border-radius: 12px
  background-color: white
  transition: all 0.2s ease
  display: flex
  align-items: center
  justify-content: center

  &.valid
    border-color: #3b82f6
    background-color: #eff6ff

  &.error
    border-color: #ef4444
    background-color: #fef2f2

  ::v-deep input
    width: 100%
    font-size: 28px
    line-height: 36px
    letter-spacing: -1%
    font-weight: 500
    padding: 0px
    height: 54px
    color: #6b7280
    border: none
    background: transparent
    text-align: center
    padding: 0
    margin: 0
    -webkit-appearance: none
    -moz-appearance: textfield

    &:focus
      outline: none

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button
      -webkit-appearance: none
      margin: 0
      display: none

.log-mindfulness-form-controls
  margin-top: 20px

.disabled-icon
  opacity: 0.4
  cursor: not-allowed
  pointer-events: none


.log-tracking-btn
  display: flex
  align-items: center
  justify-content: center
  width: 32px
  height: 32px

.error-message
  width: 100%
  text-align: left
  font-size: 13px
  color: #EC493F
  margin-top: 10px
  line-height: 18px
</style>
